/**
 * B6 · LEVELS — path and offer hooks.
 *
 * The AUTHORITY is the server: what's unlocked isn't recomputed here. If
 * the client did, it would say something different the moment either
 * implementation changed — and this would be the one that got it wrong in
 * front of the student. The only thing derived on the client is the DRAWING
 * (which connector is solid and which is dashed), which comes from the
 * graph the server already sends.
 */
import { createSafeQuery } from "@/lib/api";
import { useQueryClient, type UseQueryResult } from "@tanstack/solid-query";
import type { StageStatus, TrackState } from "@space/shared";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import {
  dismissOffer,
  fetchMyOffers,
  fetchMyState,
  fetchTracks,
  type PendingOffer,
  progressionQueryKeys,
  type TracksResponse,
} from "./api";

/** PUBLISHED tracks of the active academy (a student never sees drafts). */
export function useTracks(): UseQueryResult<TracksResponse> {
  const { activeOrgId } = useActiveOrg();
  return createSafeQuery<TracksResponse>(() => ({
    queryKey: progressionQueryKeys.tracks(activeOrgId(), false),
    queryFn: async () => fetchTracks(activeOrgId()!),
    enabled: Boolean(activeOrgId()),
  }));
}

/** The student's own state on a track. */
export function useTrackState(trackId: () => string | undefined): UseQueryResult<TrackState> {
  const { activeOrgId } = useActiveOrg();
  return createSafeQuery<TrackState>(() => ({
    queryKey: progressionQueryKeys.myState(activeOrgId(), trackId()),
    queryFn: async () => fetchMyState(activeOrgId()!, trackId()!),
    enabled: Boolean(activeOrgId()) && Boolean(trackId()),
  }));
}

/** Active offers. They persist until they expire or the student dismisses them. */
export function useOffers(): {
  query: UseQueryResult<PendingOffer[]>;
  dismiss: (offerId: string) => Promise<void>;
} {
  const { activeOrgId } = useActiveOrg();
  const client = useQueryClient();
  const query = createSafeQuery<PendingOffer[]>(() => ({
    queryKey: progressionQueryKeys.offers(activeOrgId()),
    queryFn: async () => fetchMyOffers(activeOrgId()!),
    enabled: Boolean(activeOrgId()),
  }));
  const dismiss = async (offerId: string): Promise<void> => {
    const org = activeOrgId();
    if (!org) {
      return;
    }
    await dismissOffer(org, offerId);
    await client.invalidateQueries({ queryKey: progressionQueryKeys.offers(org) });
  };
  return { query, dismiss };
}

/** A row of the path: the stage plus how it connects to the previous one. */
export interface PathRow {
  stageKey: string;
  stageId: string;
  title: string;
  status: StageStatus;
  blockedBy?: string | null;
  progressPct: number;
  /** Side of the snake (alternates; on mobile the amplitude shrinks via CSS). */
  side: "center" | "left" | "right";
  /**
   * Connector to the previous stage:
   *   `none` — there's no previous one;
   *   `hard` — prerequisite with no alternative;
   *   `soft` — there's another way in (this is how a level skip is drawn:
   *            the interface doesn't know the concept "skip", it just
   *            paints the derived graph);
   *   `done` — already walked.
   */
  link: "none" | "hard" | "soft" | "done";
}

/** Statuses that count as "already walked segment" when drawing the connector. */
const DONE: ReadonlySet<StageStatus> = new Set<StageStatus>(["completed", "certified"]);

/** Converts the server's state into drawable rows. Pure and testable. */
export function toPathRows(state: TrackState | undefined): PathRow[] {
  if (!state) {
    return [];
  }
  const ordered = [...state.stages].toSorted((a, b) =>
    a.position === b.position ? (a.stageKey < b.stageKey ? -1 : 1) : a.position - b.position,
  );
  return ordered.map((stage, index) => {
    const previous = index > 0 ? ordered[index - 1] : undefined;
    const incoming = state.edges.filter((e) => e.toStageKey === stage.stageKey);
    let link: PathRow["link"] = "none";
    if (previous) {
      if (DONE.has(previous.status)) {
        link = "done";
      } else if (incoming.length > 0 && incoming.every((e) => !e.hard)) {
        link = "soft";
      } else {
        link = incoming.length > 0 ? "hard" : "done";
      }
    }
    return {
      stageKey: stage.stageKey,
      stageId: stage.stageId,
      title: stage.title,
      status: stage.status,
      blockedBy: stage.blockedBy,
      progressPct: stage.progressPct,
      side: index === 0 ? "center" : index % 2 === 1 ? "left" : "right",
      link,
    };
  });
}
